// vendors
import Vue from 'vue';
import VueModal from 'vue-js-modal';

// misc
import { i18n, loadDefaultLanguage } from '@v4/locale/async';
import { getQueryParams } from '@v4/common/utils/url';
import { getJsonDataFromScript } from '@v4/common/utils/json';
import { getPrismImageUrl } from '@v4/common/utils/image';
import { swiperConfig4Slides } from '@v4/config';
import { isEmpty } from 'lodash';

// mixins
import { dkModalAware } from '@v4/common/components/mixins/modal';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import clickoutPopupEventsHandler from '@v4/common/components/mixins/clickout-popup-events-handler';
import clickToCallModalHandler from '@v4/common/components/mixins/click-to-call-modal-handler';
import { EventBus } from '@v4/common/utils/event-bus';
import lazyLoadInit from '@v4/common/utils/lazyload-init';
import { modalWizardLeadForm } from '@v4/common/components/mixins/modal-service';

// utils
import '@v4/common/utils/lazy-load';
import { CLICK_TO_CALL_EVENTS, CLICK_TO_CALL_EVENT_SOURCES } from '@v4/services/api/click-to-call/click-to-call';
import { isMobile } from '@v4/common/utils/media-query';

(async () => {
    if (!document.getElementById('vdp')) {
        return;
    }

    Vue.use(VueModal, { dynamic: true, injectModalsContainer: true });
    await loadDefaultLanguage();

    lazyLoadInit();

    const fetchedData = getJsonDataFromScript('#vdp-data') || {};

    const productViewer = (fetchedData.media && fetchedData.media.productViewer) || {};
    const productViewerImages360 = productViewer.images360 || {};
    const mediaGallery = (fetchedData.media && fetchedData.media.gallery) || [];

    const components = {
        MediaCounter: () => ({
            component: import(
                /* webpackChunkName: "MediaCounter-vue", webpackPreload: true */ '@v4/common/components/molecules/DkMediaCounterDST.vue'
            ),
        }),
        DkColorSelection: () => ({
            component: import(
                /* webpackChunkName: "DkColorSelection-vue" */ '@/js/vdp/components/molecules/DkColorSelectionDST.vue'
            ),
        }),
        InView: () => ({
            component: import(/* webpackChunkName: "InView-vue" */ '@v4/common/components/atoms/InView'),
        }),
        DkRatingDst: () => ({
            component: import(
                /* webpackChunkName: "DkRatingDst-vue", webpackPreload: true */ '@v4/common/components/molecules/DkRatingDST.vue'
            ),
        }),
        DkSlide: () => ({
            component: import(
                /* webpackChunkName: "DkSlide-vue", webpackPreload: true */ '@v4/common/components/atoms/DkSlide'
            ),
        }),
        DkSlider: () => ({
            component: import(
                /* webpackChunkName: "DkSliderDST-vue", webpackPreload: true */ '@v4/common/components/molecules/DkSliderDST.vue'
            ),
        }),
        LazyOffersDst: () => ({
            component: import(
                /* webpackChunkName: "LazyOffersDst-vue", webpackPreload: true */ '@v4/vdp/components/molecules/LazyOffersDST.vue'
            ),
        }),
        ClickoutBox: () => ({
            component: import(
                /* webpackChunkName: "ClickoutBox-vue" */ '@v4/common/components/molecules/ClickoutBox.vue'
            ),
        }),
        DkButton: () => ({
            component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton'),
        }),
        DkReadMore: () => ({
            component: import(/* webpackChunkName: "DkReadMore-vue" */ '@v4/common/components/atoms/DkReadMoreDST'),
        }),
        DkBadge: () => ({
            component: import(/* webpackChunkName: "DkBadgeDST-vue" */ '@v4/common/components/atoms/DkBadgeDST.vue'),
        }),
        VideoSwiper: () => ({
            component: import(
                /* webpackChunkName: "VideoSwiper-vue" */ '@/js/vdp/components/youtube-gallery/VideoSwiper.vue'
            ),
        }),
        DkIcon: () => ({
            component: import(/* webpackChunkName: "Icon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
        ImageSwiper: () => ({
            component: import(
                /* webpackChunkName: "ImageSwiper-vue" */ '@/js/vdp/components/youtube-gallery/ImageSwiper.vue'
            ),
        }),
        DkClickToCallSnackbar: () => ({
            component: import(
                /* webpackChunkName: "ClickToCallSnackbar-vue" */ '@/js/common/components/organisms/click-to-call/DkClickToCallSnackbar.vue'
            ),
        }),
        DkUspOverlay: () => ({
            component: import(
                /* webpackChunkName: "DkUspOverlay-vue", webpackPreload: true */ '@v4/common/components/atoms/DkUSPOverlay.vue'
            ),
        }),
    };

    if (productViewerImages360.available) {
        components.ProductViewerButtonDst = () => ({
            component: import(
                /* webpackChunkName: "ProductViewerButtonDST-vue", webpackPreload: true */ '@v4/common/components/atoms/ProductViewerButtonDST.vue'
            ),
        });
    }

    if (mediaGallery.length) {
        components.DkMediaSwipeViewer = () => ({
            component: import(
                /* webpackChunkName: "DkMediaSwipeViewer-vue", webpackPreload: true */ '@v4/common/components/organisms/DkMediaSwipeViewer/DkMediaSwipeViewer.vue'
            ),
        });
    }

    if (fetchedData.hasSuggestions) {
        components.LazySimilarCarsDst = () => ({
            component: import(
                /* webpackChunkName: "LazySimilarCarsDST-vue", webpackPreload: true */ '@v4/vdp/components/molecules/LazySimilarCarsDST.vue'
            ),
        });
    }

    new Vue({
        el: '#vdp',
        name: 'VehicleDetailPage',
        i18n,
        components,
        directives: {
            intersect: {
                inserted(el, binding) {
                    const options = { root: null, threshold: 0 };
                    const observer = new IntersectionObserver((entries) => {
                        entries.forEach((entry) => {
                            // Call the provided callback with the intersection entry
                            binding.value(entry);
                        });
                    }, options);
                    observer.observe(el);
                    el._observer = observer;
                },
                unbind(el) {
                    if (el._observer) {
                        el._observer.disconnect();
                    }
                },
            },
        },
        mixins: [
            dkModalAware,
            gaEventHandler,
            clickoutPopupEventsHandler,
            modalWizardLeadForm,
            clickToCallModalHandler,
        ],
        data() {
            return {
                fetchedData,
                productViewerProps: {
                    translations:
                        (this.$i18n.messages[this.$i18n.locale] &&
                            this.$i18n.messages[this.$i18n.locale]['product-viewer']) ||
                        {},
                    images: JSON.parse(productViewerImages360.closed || '[]'),
                    imagesOpen: JSON.parse(productViewerImages360.open || '[]'),
                    start: productViewerImages360.start || 0,
                    interior: getPrismImageUrl(productViewer.panorama, 4096),
                    disclaimerText: productViewer.disclaimer,
                    hintText: productViewer.hint,
                    popupClickoutObserver: null,
                },
                mediaSwipeProps: {
                    swipeOptions: { history: false },
                    items: mediaGallery,
                },
                mediaSwipeEvents: {
                    init: this.onMediaSwipeInit,
                    close: this.onMediaSwipeClose,
                },
                hasSuggestions: fetchedData.hasSuggestions || true,
                hasOffers: true,
                trimSwiperOptions: swiperConfig4Slides,
                imageSourceForGallery: '',
                // data properties for Video on VDP feature
                isYoutubeScriptLoaded: false,
                videoInVDPComponent: null,
                isVideoMuted: true,
                isMobile: isMobile(),
                showStickyCtas: false,
                selectedColor: null,
            };
        },
        computed: {
            showClickoutBox() {
                return this.fetchedData.clickout_section && !isEmpty(this.fetchedData.clickout_section);
            },
            galleryContentAmount() {
                return this.fetchedData.media.gallery.length + 1;
            },
            videoInVDPSwiperOpts() {
                return {
                    loopedSlides: this.galleryContentAmount,
                };
            },
            youtubeMediaSwipeProps() {
                return {
                    swipeOptions: { history: false },
                    items: mediaGallery,
                    youtubeId: this.youtubeId,
                    isSoundOff: this.isVideoMuted,
                    areControlsVisible: !this.isMobile,
                    uspData: this.fetchedData.uspData,
                };
            },
            videoInVDPRefDkMediaSwipe() {
                return (
                    (this.videoInVDPComponent &&
                        this.videoInVDPComponent.$refs &&
                        this.videoInVDPComponent.$refs.youtubeMediaSwipe) ||
                    {}
                );
            },
            youtubeId() {
                return this.fetchedData.media.youtubeVideo.reference;
            },
        },
        mounted() {
            import(
                /* webpackChunkName: "DkReadMore-vue", webpackPreload: true */ '@v4/common/components/atoms/DkReadMore'
            ).then(({ default: DkReadMore }) => DkReadMore().init());

            const shouldShowLeadForm = getQueryParams().show_lead_form || getQueryParams().lead_error;

            if (shouldShowLeadForm) {
                const showPriceTab = true;
                this.showLeadFormModal({ showPriceTab });
            }

            if (!shouldShowLeadForm && this.fetchedData.showClickToCallPopupTimed) {
                this.showClickToCallPopup();
            }

            EventBus.$on('VDPOffers:clickShowMore', this.handleOfferShowMoreClick);
            EventBus.$on('LeadForm:input', this.onLeadFormInput);

            if (this.fetchedData && this.fetchedData.events && this.fetchedData.events['view_item']) {
                this.pushGtagEvent('view_item', null, this.fetchedData.events);
            }

            const makeModel = `${this.fetchedData.makeName} ${this.fetchedData.submodelCommercialName}`;
            this.bindPopupClickoutTracking('Vehicle Detail Page', makeModel);

            if (this.youtubeId) {
                // load the script
                this.loadYoutubeScript();
            }

            // hiding overflow on the document's body because of the infinite gallery slider
            const body = document.getElementsByTagName('body')[0];
            body.style.overflow = 'hidden';
        },
        beforeDestroy() {
            EventBus.$off();
        },
        methods: {
            onPriceBtnClick(params = {}) {
                this.showLeadFormModal(params);

                this.pushGaEvent(
                    'Lead form',
                    'Open form (new VDP)',
                    'Click on CTA discover the price on top of the page'
                );
                this.pushGtagEvent('add_to_cart', null, this.fetchedData.events);
            },
            onConfigureBtnClick() {
                this.pushGaEvent(
                    'Vehicle detail page',
                    'Open configurator (new VDP)',
                    'Click on CTA configure your car on top the page'
                );
                this.handleOpenConfigurator();
            },
            onProductViewerEvent(eventName, data) {
                const args = ['product-viewer-360', eventName];

                if (data) {
                    args.push(data);
                }

                const category = args[0] || '';
                const action = args[1] || '';
                const label = args[2] || '';

                this.pushGaEvent(category, `${action} (new VDP)`, label);
            },
            onMediaSwipeInit() {
                this.$el.classList.add('multi-section--gallery-visible');
            },
            onMediaSwipeClose() {
                this.$el.classList.remove('multi-section--gallery-visible');
            },
            onMediaGalleryShown(source, { photoSwipe, isClickOnHeroImage }) {
                if (photoSwipe && typeof photoSwipe.goTo === 'function') {
                    const isGalleryFetched = this.fetchedData.media && this.fetchedData.media.gallery.length;
                    const { gallery } = isGalleryFetched ? this.fetchedData.media : [];
                    const index = gallery.findIndex((media) => {
                        if (isClickOnHeroImage) {
                            return media[source] === this.imageSourceForGallery;
                        } else {
                            return media[source];
                        }
                    });

                    index && photoSwipe.goTo(index);
                }
            },
            toggleMediaGallery(source) {
                if (source) {
                    this.imageSourceForGallery = source;
                    EventBus.$emit('Hero-image:open-gallery');
                }
            },
            onColorSelectionPriceBtnClick() {
                this.showLeadFormModal();
                this.pushGaEvent(
                    'Lead form',
                    'Open form (new VDP)',
                    'Click on CTA discover the price in the middle of the page'
                );
                this.pushGtagEvent('add_to_cart', null, this.fetchedData.events);
            },
            onColorSelectionConfigurationBtnClick() {
                this.pushGaEvent(
                    'Vehicle detail page',
                    'Open configurator (new VDP)',
                    'Click on CTA configure your car on middle the page'
                );

                this.pushGtagEvent('click_open_configurator', null, this.fetchedData.events);
            },
            onSeeAllVersionBtnClick() {
                this.pushGaEvent(
                    'Vehicle detail page',
                    'Open version list page (new VDP)',
                    'Open Version list page (in trims section)'
                );
            },
            onAlternatePriceBtnClick() {
                this.showLeadFormModal();
                this.pushGaEvent(
                    'Lead form',
                    'Open form (new VDP)',
                    'Click on CTA discover the price in the blue section'
                );
                this.pushGtagEvent('add_to_cart', null, this.fetchedData.events);
            },
            onArticleReadMoreBtnClick() {
                this.pushGaEvent(
                    'Vehicle detail page',
                    'Click on Read more (new VDP)',
                    'Click on Read more in editorial section'
                );
            },
            onArticleSeeAllBtnClick() {
                this.pushGaEvent(
                    'Vehicle detail page',
                    'Click on See all guides (new VDP)',
                    'Click on See all guides in editorial section'
                );
            },
            onSimilarCarsResponse(...args) {
                const { response } = args[0];
                this.hasSuggestions = !!(response && response.items && response.items.length);
            },
            onSimilarCarsError() {
                this.hasSuggestions = false;
            },
            onOffersResponse(...args) {
                const { response } = args[0];

                if (response && response.data && response.data.items && response.data.items.length) {
                    const offers =
                        response.data.items.filter(
                            (item) => item.type && item.type.slug && item.type.slug.toLowerCase() !== 'listing'
                        ) || [];

                    this.hasOffers = !!offers.length;
                    return;
                }
                this.hasOffers = false;
            },
            onOffersError() {
                this.hasOffers = false;
            },
            handleOfferShowMoreClick(offerId) {
                this.pushGtagEvent('select_promotion', offerId, this.fetchedData.events);
            },
            onTrimClick(trimName) {
                this.pushGtagEvent('click_open_trim', null, null, {
                    make: this.fetchedData.makeName,
                    submodel: this.fetchedData.description,
                    // eslint-disable-next-line camelcase,
                    submodel_id: this.fetchedData.formData.submodelId,
                    // eslint-disable-next-line camelcase
                    equipment_type: trimName,
                });
            },
            onLeadFormInput() {
                this.pushGtagEvent('begin_checkout', null, this.fetchedData.events);
            },
            handleOpenConfigurator() {
                this.pushGtagEvent('click_open_configurator', null, this.fetchedData.events);
            },
            // methods for Video on VDP feature
            loadYoutubeScript() {
                const script = document.createElement('script');
                script.src = 'https://www.youtube.com/iframe_api';
                script.id = 'video-vdp-youtube';
                const firstScriptTag = document.getElementsByTagName('script')[0];
                firstScriptTag.parentNode.insertBefore(script, firstScriptTag);

                window.onYouTubePlayerAPIReady = async () => {
                    this.isYoutubeScriptLoaded = true;
                };
            },
            showMediaGallery(index) {
                this.defineAsyncComponent().then(() => {
                    if (typeof this.videoInVDPRefDkMediaSwipe.show !== 'function') {
                        return;
                    }

                    if (this.youtubeId) {
                        EventBus.$emit('YoutubeMediaGallery:open-gallery');
                    }

                    this.videoInVDPRefDkMediaSwipe.show(index);
                });
            },
            defineAsyncComponent() {
                return new Promise((resolve, reject) => {
                    if (this.videoInVDPComponent) {
                        return resolve();
                    }

                    import(
                        /* webpackChunkName: "YoutubeMediaSwipe-vue" */ '@/js/vdp/components/youtube-gallery/YoutubeMediaSwipe.vue'
                    )
                        .then(({ default: YoutubeMediaSwipe }) => {
                            const div = document.createElement('div');
                            this.$el.appendChild(div);

                            this.videoInVDPComponent = new Vue({
                                parent: this.$root,
                                render: (h) =>
                                    h(YoutubeMediaSwipe, {
                                        props: this.youtubeMediaSwipeProps,
                                        ref: 'youtubeMediaSwipe',
                                        on: {
                                            ...this.$listeners,
                                            'hook:mounted': resolve,
                                        },
                                    }),
                            }).$mount(div);
                        })
                        .catch(reject);
                });
            },
            setAudioState(isMuted) {
                if (this.isVideoMuted !== isMuted) {
                    this.isVideoMuted = isMuted;
                }
            },
            onClickToCall(clickSource) {
                this.clickSource = clickSource;
                this.trackClickToCallClick(CLICK_TO_CALL_EVENTS.trigger);

                if (this.clickSource !== CLICK_TO_CALL_EVENT_SOURCES.popup) {
                    switch (this.clickSource) {
                        case CLICK_TO_CALL_EVENT_SOURCES.cta:
                            this.clickSource = CLICK_TO_CALL_EVENT_SOURCES.ctaPopup;
                            break;
                        default:
                            this.clickSource = CLICK_TO_CALL_EVENT_SOURCES.snackbarPopup;
                    }
                }

                this.handleClickToCallModal();
            },
            showLeadFormModal({ color = null, showPriceTab = false, forceShowColorsTab = false } = {}) {
                const {
                    formData,
                    formId,
                    fullName,
                    makeUrlCode: make,
                    submodelCommercialUrlCode: model,
                    minPrice: price,
                    customCtaFormWizard: customCta,
                } = this.fetchedData;

                const selectedColor = color || this.selectedColor;

                const skipColorsTab = !!(this.selectedColor && !forceShowColorsTab);

                const props = {
                    formData,
                    formId,
                    fullName,
                    make,
                    model,
                    price,
                    showPriceTab,
                    customCta,
                    selectedColor,
                    skipColorsTab,
                };

                this.clearClickToCallTimer();
                this.openWizardLeadFormModal(props);
            },
            onColorSelect(color) {
                this.showLeadFormModal({ color });
            },
            onColorSelectFromHead(color) {
                this.selectedColor = color;
            },
            handleIntersection(entry) {
                // Set the flag to true when the in-page CTAs are not intersecting (i.e. not visible)
                this.showStickyCtas = !entry.isIntersecting;
                document.body.classList.toggle('has--vdp-sticky-ctas', this.showStickyCtas);
            },
        },
    });

    if (document.getElementById('header')) {
        import(/* webpackChunkName: "header" */ '@v4/header').then(({ default: Header }) => Header());
    }
})();
